import React, { useEffect, useState } from "react";
import {
  ThemeProvider,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import {
  getCacheTechnicians,
  getTechnician,
  deleteTechnician,
} from "../API/technicianAPI";
import EventIcon from "@mui/icons-material/Event";

import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
      error: "#cc0000", // Custom error color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function DeleteTechniciansComponent() {
  const [techniciansCache, setTechniciansCache] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null); // Default null to represent no selection
  const [technician, setTechnician] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [isFetchingTechnician, setIsFetchingTechnician] = useState(false); // Prevent double fetch for technician
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const competencyLabels = [
    { label: "General Pest Control", key: "general_pest_control" },
    { label: "Termite Control", key: "termite_control" },
    { label: "QPMS", key: "qpms" },
    { label: "Disinfection", key: "disinfection" },
    { label: "Basic Safety", key: "basic_safety" },
    { label: "Driving (2 wheels)", key: "driving_2_wheels" },
    { label: "Driving (4 wheels)", key: "driving_4_wheels" },
  ];

  const professionalLabels = [
    {
      category: "GPC",
      items: [
        { label: "Rodent", key: "rodent" },
        { label: "Baiting", key: "baiting" },
        { label: "Cockroach", key: "cockroach" },
        { label: "Fly Control", key: "fly_control" },
        { label: "Ant Control", key: "ant_control" },
        { label: "Larviciding", key: "larviciding" },
      ],
    },
    {
      category: "Termite",
      items: [
        { label: "Compre", key: "compre" },
        { label: "AG", key: "ag" },
        { label: "IG", key: "ig" },
        { label: "IC", key: "ic" },
        { label: "Spraying", key: "spraying" },
        { label: "Soil Poisoning", key: "soil_poisoning" },
      ],
    },
  ];

  // Fetch cache technicians on component mount
  useEffect(() => {
    const fetchCacheTechnicians = async () => {
      try {
        const techniciansCache = await getCacheTechnicians();
        setTechniciansCache(techniciansCache || []); // Ensure it defaults to an empty array if null
      } catch (err) {
        setError("Failed to fetch technicians cache");
      }
    };

    fetchCacheTechnicians();
  }, []); // Empty dependency array ensures this only runs once

  const handleSearchTechnician = (event, value) => {
    setIsFormVisible(false);
    setSelectedTechnician(value || null); // Update the selected technician
  };

  const handleSearchButtonClick = async () => {
    if (!selectedTechnician) {
      console.log("No technician selected");
      return;
    }

    if (isFetchingTechnician) {
      console.log("Already fetching technician details...");
      return;
    }

    setIsFetchingTechnician(true);
    setIsLoading(true);

    try {
      const fetchedTechnician = await getTechnician(selectedTechnician.id);
      setTechnician(fetchedTechnician);
      setIsFormVisible(true);
    } catch (error) {
      console.error("Error fetching technician details:", error);
    } finally {
      setIsFetchingTechnician(false);
      setIsLoading(false);
    }
  };

  const options = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const competencyLevels = [
    "N/A",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
  ];
  const professionalLevels = [
    "Team Leader",
    "High",
    "Mid",
    "Basic",
    "For observation",
  ];

  const handleCompetencyLevel = (field, value) => {
    setTechnician((prev) => ({
      ...prev,
      competency_level: {
        ...prev.competency_level, // Spread the existing fields in competency_level
        [field]: value, // Update or add the specific field
      },
    }));
  };

  const handleProfessionalLevel = (category, field, value) => {
    setTechnician((prev) => ({
      ...prev,
      professional_level: {
        ...prev.professional_level, // Preserve other categories in professional_level
        [category]: {
          ...prev.professional_level[category], // Preserve other fields in the specific category
          [field]: value, // Update the specific field
        },
      },
    }));
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (window.confirm("Are you sure you want to delete this Technician?")) {
      setIsLoading(true);

      const {
        timestamp_create,
        timestamp_update,
        ...updatedTechnicianParameters
      } = technician;

      try {
        const response = await deleteTechnician(
          technician.id,
          updatedTechnicianParameters
        );
        console.log("Technician delete response:", response);

        if (response) {
          setSuccessMessage("Technician deleted successfully!");

          setTechniciansCache((prevCache) =>
            prevCache.filter((tech) => tech.id !== technician.id)
          );

          setSelectedTechnician("");
          setIsFormVisible(false);
        } else {
          setErrorMessage(
            "An unexpected error occurred while deleting the Technician. Please try again later."
          );
        }
      } catch (error) {
        setErrorMessage(
          "An error occurred while deleting the Technician. Please check your network connection and try again."
        );
        console.error("Error occurred during submission:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSuccessMessage("")} // Clear the success message on close
      >
        <Alert
          severity="success"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Loading Snackbar */}
      <Snackbar
        open={isLoading}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          Loading, please wait...
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert
          severity="error"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ padding: "40px 60px" }}>
        <Card
          variant="outlined"
          style={{
            marginTop: "40px",
            marginBottom: "40px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <EventIcon
                    sx={{
                      fontSize: "40px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="h6">Admin Hub</Typography>
                </Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ color: "white" }}
                >
                  <Typography sx={{ color: "#ffffff99" }}>Admin Hub</Typography>
                  <Typography
                    component={Link}
                    to="/delete-technicians"
                    sx={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    Delete Technicians
                  </Typography>
                </Breadcrumbs>
              </Box>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: "16px",
            }}
          />
          <CardContent sx={{ padding: "20px" }}>
            <Card variant="outlined" sx={{ padding: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Delete Technicians
                  </Typography>
                }
                sx={{ marginBottom: 2 }}
              />
              <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Autocomplete
                      freeSolo
                      options={techniciansCache}
                      getOptionLabel={(technician) => technician?.name || ""} // Fallback to an empty string
                      value={selectedTechnician} // Controlled component
                      onChange={(event, newValue) =>
                        handleSearchTechnician(event, newValue)
                      } // Updates selected value
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Technician Name"
                          variant="outlined"
                          fullWidth
                          error={!!error} // Displays error styling if there is an error
                          helperText={error || ""} // Displays error message
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    direction={{ xs: "column", sm: "row" }}
                    container
                    alignItems="center"
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearchButtonClick} // Call button handler
                      disabled={isLoading} // Disable button while loading
                      sx={{
                        mb: { xs: 0, md: 0, sm: 2 },
                        mr: { xs: 0, md: 3, sm: 3 },
                      }}
                    >
                      {isLoading ? "Searching..." : "Search"}{" "}
                      {/* Change button label dynamically */}
                    </Button>
                    {isFormVisible && (
                      <Button
                        variant="contained"
                        onClick={handleSubmit} // Call button handler
                        color="error"
                        disabled={isLoading}
                      >
                        Delete Record
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <StyledHr />
              {isFormVisible && technician && (
                <form className="technician-form" autoComplete="off">
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: "flex", flexDirection: "column" }}
                    >
                      <Card
                        variant="outlined"
                        sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                      >
                        <CardHeader
                          title={
                            <Typography
                              variant="h6"
                              sx={{
                                paddingBottom: "1rem",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                fontSize: "1rem",
                              }}
                            >
                              Basic Information
                            </Typography>
                          }
                        />
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Grid container spacing={3} sx={{ flex: 1 }}>
                            <Grid item xs={6}>
                              <TextField
                                name="firstname"
                                label="Firstname"
                                value={technician.firstname}
                                fullWidth
                                size="small"
                                InputProps={{ sx: { fontSize: "0.875rem" } }}
                                InputLabelProps={{
                                  sx: { fontSize: "0.875rem" },
                                }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                name="lastname"
                                label="Lastname"
                                value={technician.lastname}
                                fullWidth
                                size="small"
                                InputProps={{ sx: { fontSize: "0.875rem" } }}
                                InputLabelProps={{
                                  sx: { fontSize: "0.875rem" },
                                }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel
                                  id="team-label"
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  Team
                                </InputLabel>
                                <Select
                                  labelId="team-label"
                                  name="type"
                                  value={technician.team}
                                  label="Team"
                                  sx={{ fontSize: "0.875rem" }}
                                  disabled
                                >
                                  <MenuItem value="Delta">Delta</MenuItem>
                                  <MenuItem value="Sigma">Sigma</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              <StyledHr />
                            </Grid>

                            <Grid item xs={12}>
                              <FormGroup>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    fontSize: "1rem",
                                    marginBottom: "2rem",
                                  }}
                                >
                                  Rest Days
                                </Typography>

                                <Grid container spacing={2} flexWrap>
                                  {options.map((option) => (
                                    <Grid item key={option}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={technician.rest_day.includes(
                                              option
                                            )}
                                            value={option}
                                          />
                                        }
                                        label={option}
                                        disabled
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </FormGroup>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card
                        variant="outlined"
                        sx={{ padding: "20px", marginBottom: "20px" }}
                      >
                        <CardHeader
                          title={
                            <Typography
                              variant="h6"
                              sx={{
                                paddingBottom: "1rem",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                fontSize: "1rem",
                              }}
                            >
                              Competency Level
                            </Typography>
                          }
                        />
                        <CardContent>
                          {competencyLabels.map(({ label, key }) => (
                            <Grid
                              container
                              spacing={2}
                              alignItems="center"
                              sx={{ marginBottom: 3 }}
                              key={key}
                            >
                              <Grid item xs={3}>
                                <Typography variant="subtitle1" gutterBottom>
                                  {label}
                                </Typography>
                              </Grid>
                              <Grid item xs={9}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={3}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {competencyLevels.includes("N/A") && (
                                            <Button
                                              variant={
                                                technician.competency_level[
                                                  key
                                                ] === "N/A"
                                                  ? "contained"
                                                  : "outlined"
                                              }
                                              onClick={() =>
                                                handleCompetencyLevel(
                                                  key,
                                                  "N/A"
                                                )
                                              }
                                              color="primary"
                                              sx={{
                                                minWidth: "40px",
                                                height: "40px",
                                                borderRadius: "8px",
                                              }}
                                              disabled
                                            >
                                              N/A
                                            </Button>
                                          )}
                                        </Box>
                                      </Grid>
                                      <Grid item xs={9}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {competencyLevels
                                            .filter((value) => value !== "N/A")
                                            .map((value) => (
                                              <Button
                                                key={value}
                                                variant={
                                                  technician.competency_level[
                                                    key
                                                  ] === value
                                                    ? "contained"
                                                    : "outlined"
                                                }
                                                onClick={() =>
                                                  handleCompetencyLevel(
                                                    key,
                                                    value
                                                  )
                                                }
                                                color="primary"
                                                sx={{
                                                  minWidth: "40px",
                                                  height: "40px",
                                                  borderRadius: "8px",
                                                }}
                                                disabled
                                              >
                                                {value}
                                              </Button>
                                            ))}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        {professionalLabels.map((category) => (
                          <Grid item xs={12} sm={6} key={category.category}>
                            <Card
                              variant="outlined"
                              sx={{ padding: "20px", marginBottom: "20px" }}
                            >
                              <CardHeader
                                title={
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      paddingBottom: "1rem",
                                      fontWeight: "bold",
                                      textTransform: "uppercase",
                                      fontSize: "1rem",
                                    }}
                                  >
                                    Professional Level ({category.category})
                                  </Typography>
                                }
                              />
                              <CardContent>
                                {category.items.map((item) => (
                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                    sx={{ marginBottom: 3 }}
                                    key={item.key}
                                  >
                                    <Grid item xs={3}>
                                      <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          flexWrap: "nowrap",
                                        }}
                                      >
                                        {item.label}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {professionalLevels.map((value) => (
                                          <Button
                                            key={value}
                                            variant={
                                              technician.professional_level[
                                                category.category.toLowerCase()
                                              ][item.key] === value
                                                ? "contained"
                                                : "outlined"
                                            }
                                            color="primary"
                                            onClick={() =>
                                              handleProfessionalLevel(
                                                category.category.toLowerCase(),
                                                item.key,
                                                value
                                              )
                                            }
                                            sx={{
                                              minWidth: {
                                                xs: "30px",
                                                sm: "35px",
                                                md: "40px",
                                              },
                                              height: {
                                                xs: "30px",
                                                sm: "35px",
                                                md: "40px",
                                              },
                                              borderRadius: "8px",
                                              fontSize: {
                                                xs: "0.75rem",
                                                sm: "0.75rem",
                                                md: "0.75rem",
                                              },
                                            }}
                                            disabled
                                          >
                                            {value}
                                          </Button>
                                        ))}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                ))}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}

export default DeleteTechniciansComponent;
